import { useEffect, useState } from 'react';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import { getArtworkPurchaseGalleryProfile } from '@/pages/artwork/utils/artworkView.selectors';
import UserService from '@/services/user.service';
import AddShippingAddress from '@/shared-components/forms/components/AddShippingAddress';
import ThumbIcon from '@/shared-components/icons/components/artwork/ThumbIcon';
import EditIcon from '@/shared-components/icons/components/new-icons/EditIcon';
import ShippingDetailsPurchaseLoader from '@/shared-components/loader/special-loaders/ShippingDetailsPurchaseLoader';
import AutoMarquee from '@/shared-components/text/components/AutoMarquee';

import ShippingAddressSelectorPopup from './components/ShippingAddressSelectorPopup';
import Reviews from './reviews';

import './index.css';

export default function ShippingDetails({ artworkData, formState, setFormState, typeOfPurchase }) {
  const t = useTranslations('common.shared.artwork_view.shipping_details');
  const galleryProfile = getArtworkPurchaseGalleryProfile(artworkData);
  const { state, stateHandler } = useStateHandler();
  const { loading, fetchData, setFetchData } = useCache(
    `artwork_${artworkData.slug}_payment_details_${typeOfPurchase}`,
    `${config.apis.api.url}/payment/${artworkData._id}`,
    {
      injectToken: true,
      expiresIn: ["2", "minutes"],
      includeSearchQueries: { type: typeOfPurchase },
    }
  );
  const [openAddAddressForm, setOpenAddAddressForm] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [openReviews, setOpenReviews] = useState(false);

  // Defensive fallbacks to handle initial undefined fetchData or absent fields
  const safeData = fetchData || {};
  const userAddresses = Array.isArray(safeData.user_addresses) ? safeData.user_addresses : [];
  const valorations = safeData.valorations || { total: 0, average: 0 };
  const sessionPreferredId = state.user_session?.preferred_shipping_address_id ?? null;
  const selectedAddressId = formState.shipping_details?._id ?? null;

  function resolvePreferredAddress(addresses) {
    if (!Array.isArray(addresses) || addresses.length === 0) {
      return null;
    }

    const sessionPreferred = sessionPreferredId
      ? addresses.find(address => address._id === sessionPreferredId)
      : null;

    if (sessionPreferred) {
      return sessionPreferred;
    }

    const flagged = addresses.find(address => (
      address?.address_is_default
      || address?.address_is_preferred
      || address?.isPreferred
      || address?.preferred
    ));

    return flagged ?? addresses[addresses.length - 1];
  }

  // Propagate the preferred id to the session store and API so every screen stays aligned.
  function persistPreferredAddress(addressId) {
    const normalizedId = addressId ?? null;

    if ((state.user_session?.preferred_shipping_address_id ?? null) !== normalizedId) {
      stateHandler.set('user_session.preferred_shipping_address_id', normalizedId);
    }

    if (typeof UserService.setPreferredShippingAddress === 'function') {
      UserService.setPreferredShippingAddress(normalizedId).catch((error) => {
        console.error('persistPreferredAddress error', error);
      });
    }
  }

  useEffect(() => {
    const defaultAddress = resolvePreferredAddress(userAddresses);

    if (!defaultAddress) {
      if (selectedAddressId) {
        setFormState(prev => ({
          ...prev,
          shipping_details: null,
        }));
      }
      return;
    }

    if (!selectedAddressId || selectedAddressId !== defaultAddress._id) {
      setFormState(prev => ({
        ...prev,
        shipping_details: defaultAddress,
      }));
    }
  }, [userAddresses, sessionPreferredId, selectedAddressId, setFormState]);

  const selectedAddress = formState.shipping_details;
  const hasSelectedAddress = Boolean(selectedAddress);
  const hasAddresses = userAddresses.length > 0;

  function addShippingAddress(newAddressData) {
    setFetchData({
      ...safeData,
      user_addresses: [...userAddresses, newAddressData],
    });
    setFormState(prev => ({
      ...prev,
      shipping_details: newAddressData,
    }));
    if (newAddressData?._id) {
      persistPreferredAddress(newAddressData._id);
    }
    setIsSelectorOpen(false);
  }

  function editShippingAddress(newAddressData) {
    setFetchData({
      ...safeData,
      user_addresses: userAddresses.map((address) => (
        address._id === newAddressData._id ? newAddressData : address
      )),
    });
    setFormState(prev => ({
      ...prev,
      shipping_details: newAddressData,
    }));
    if (newAddressData?._id && selectedAddressId === newAddressData._id) {
      persistPreferredAddress(newAddressData._id);
    }
    setIsSelectorOpen(false);
  }

  function handleAddAddressClick() {
    setIsSelectorOpen(false);
    setOpenAddAddressForm(true);
  }

  function handleWrapperClick() {
    if (hasSelectedAddress) {
      setAddressToEdit(selectedAddress);
    } else if (hasAddresses) {
      setIsSelectorOpen(true);
    } else {
      handleAddAddressClick();
    }
  }

  function handleChangeAddressClick(event) {
    event.stopPropagation();
    setIsSelectorOpen(true);
  }

  function handleSelectShipping(address) {
    if (!address?._id) {
      return;
    }

    setFormState(prev => ({
      ...prev,
      shipping_details: address,
    }));
    persistPreferredAddress(address._id);
    setIsSelectorOpen(false);
  }

  return (
    <>
      <div className="purchase-shipping-details__container">
        {loading ? (
          <ShippingDetailsPurchaseLoader className="purchase-shipping-details__spinner" />
        ) : openReviews ? (
          <Reviews
            purchaseFetchData={safeData}
            artworkData={artworkData}
            typeOfPurchase={typeOfPurchase}
            close={() => setOpenReviews(false)}
          />
        ) : (
          <>
            <div className="purchase-shipping-details__shipped-by-container">
              <span className="purchase-shipping-details__shipped-by dots-on-overflow font-size-medium">
                {t('shipped_by')}{" "}
                <span>
                  {typeOfPurchase === "limited_edition"
                    ? t('suarte_name')
                    : galleryProfile.name}
                </span>
              </span>
            </div>

            {valorations.total !== 0 && (
              <div className="purchase-shipping-details__reviews-details">
                <ThumbIcon className="purchase-shipping-details__thumb-icon" />

                <span className="purchase-shipping-details__shipped-by font-size-medium">
                  {valorations.average}%
                </span>

                <span
                  className="purchase-shipping-details__reviews-link element-non-selectable font-size-medium"
                  onClick={() => setOpenReviews(true)}
                >
                  {t('reviews_count', { count: valorations.total })}
                </span>
              </div>
            )}

            <div
              className={`purchase-shipping-details__add-address-wrapper${hasSelectedAddress ? ' has-address' : ''}`}
              onClick={handleWrapperClick}
            >
              {hasSelectedAddress ? (
                <>
                  <div className="purchase-shipping-details__selected-address">
                    <div className="purchase-shipping-details__selected-address-header">
                      <div className="purchase-shipping-details__selected-address-heading">
                        <AutoMarquee
                          contentClassName="purchase-shipping-details__selected-address-heading-content"
                          dependencies={[selectedAddress.address_name, selectedAddress.address_surname]}
                        >
                          {selectedAddress.address_name} {selectedAddress.address_surname}
                        </AutoMarquee>
                      </div>
                      <button
                        type="button"
                        className="purchase-shipping-details__change-address element-non-selectable"
                        onClick={handleChangeAddressClick}
                        aria-label={t('change_address')}
                        title={t('change_address')}
                      >
                        <EditIcon className="purchase-shipping-details__change-address-icon" />
                      </button>
                    </div>
                    <span className="purchase-shipping-details__selected-address-line">
                      {selectedAddress.address_street || selectedAddress.address_address}
                    </span>
                    <span className="purchase-shipping-details__selected-address-line">
                      {selectedAddress.address_city}, {selectedAddress.address_region}, {selectedAddress.address_country}
                    </span>
                    <span className="purchase-shipping-details__selected-address-line">
                      {selectedAddress.address_phone?.address_phone_prefix} {selectedAddress.address_phone?.address_phone_number}
                    </span>
                  </div>
                </>
              ) : (
                <span className="purchase-shipping-details__add-address-text element-non-selectable font-size-medium">
                  {hasAddresses ? t('select_shipping_address') : t('add_shipping_address')}
                </span>
              )}
            </div>

            {userAddresses.length === 0 && (
              <p className="purchase-shipping-details__empty-text">
                {t('empty_prompt')}
              </p>
            )}

            <ShippingAddressSelectorPopup
              open={isSelectorOpen}
              addresses={userAddresses}
              preferredAddressId={sessionPreferredId}
              selectedAddressId={selectedAddressId}
              onSelect={handleSelectShipping}
              onClose={() => setIsSelectorOpen(false)}
              onAddNewAddress={(event) => {
                event?.stopPropagation?.();
                handleAddAddressClick();
              }}
              onEditAddress={(address) => {
                setAddressToEdit(address);
                setIsSelectorOpen(false);
              }}
            />
            <AddShippingAddress
              open={openAddAddressForm}
              close={() => setOpenAddAddressForm(false)}
              onAdd={addShippingAddress}
            />

            <AddShippingAddress
              open={Boolean(addressToEdit)}
              close={() => setAddressToEdit(null)}
              addressData={addressToEdit}
              onAdd={editShippingAddress}
              editMode
            />
          </>
        )}
      </div>
    </>
  );
}
